import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type { FactsetChartParameters } from "./types";
import type {
  AddActiveGraphCoords,
  ChartingChartInfo,
  SetActiveGraphXmlOptions,
} from "./services/factset/spreadsheetIntents";
import {
  addActiveGraph,
  getActiveGraphXml,
  getCharts,
  modifyShapes,
  getStylesets as getStylesetsViaIntentBridge,
  previewChart as previewChartViaIntentBridge,
  setActiveGraphXml,
} from "./excelHostOps";
import {
  resolveChartCreatorMode,
  runOrJoinChartCreatorReauth,
} from "./chartCreatorMode";
import {
  getStylesetsViaApi,
  previewChartViaApi,
} from "./services/factset/chartCreatorApi";
import {
  buildChartToolDescription,
  getActiveGraphEntityConstraint,
  isExplicitForwardPeRequest,
} from "./chartCapabilityRegistry";
import { normalizeActiveGraphChartParameters } from "./chartCreatorPayload";
import { foregroundCoordinator } from "./foregroundCoordinator";
import { isAbortError } from "../shared/utils/abortError";

type ActiveGraphOperation =
  | "read_active_graph"
  | "create_active_graph"
  | "edit_active_graph"
  | "list_active_graph_stylesets";

interface ActiveGraphInput {
  workbookName?: string;
  stepId?: string;
  operation?: ActiveGraphOperation;
  chartParameters?: FactsetChartParameters;
  target?: {
    sheetRef?: { index?: number; name?: string; codeName?: string };
    chartIndex?: number;
    coords?: AddActiveGraphCoords;
    refresh?: boolean;
  };
}

interface ReadChartsInput {
  workbookName?: string;
  sheetRef?: { index?: number; name?: string; codeName?: string };
  chartIndex?: number;
  detail?: "Summary" | "Full";
  includeSelectedActiveGraphXml?: boolean;
}

const nullableString = { anyOf: [{ type: "string" }, { type: "null" }] };
const nullableNumber = { anyOf: [{ type: "number" }, { type: "null" }] };
const nullableBoolean = { anyOf: [{ type: "boolean" }, { type: "null" }] };

const sheetRefSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    index: nullableNumber,
    name: nullableString,
    codeName: nullableString,
  },
};

export const FACTSET_CHART_PARAMETERS_SCHEMA = {
  type: "object",
  additionalProperties: true,
  description:
    "FactSet Chart Creator parameters. Must include chartType and non-empty tickers. " +
    "Express the time range with startDate/endDate/frequency — there is NO `period` field, " +
    "and omitting startDate yields only the last 1 year.",
  properties: {
    chartType: {
      type: "string",
      description:
        'FactSet Chart Creator chartType code (e.g. "FIN_SALES", "VAL_PE2"). ' +
        '"VAL_PE2" is trailing/LTM P/E only and must not be used for forward/NTM P/E.',
    },
    tickers: {
      type: "array",
      items: { type: "string" },
      description: 'Entity ids in SYMBOL-EXCHANGE form, e.g. ["AAPL-US"].',
    },
    companyNames: {
      type: "object",
      additionalProperties: false,
      description:
        'Pretty company-name labels for the legend/title. Set `single` to the resolved ' +
        'name(s) so the chart identifies the entity, e.g. { "single": ["Apple Inc."] }.',
      properties: {
        single: { type: "array", items: { type: "string" } },
        aggregated: { type: "array", items: { type: "string" } },
      },
    },
    frequency: {
      type: "string",
      enum: ["D", "W", "M", "Q", "CQ", "FQ", "LTMQ", "A", "CY", "FY"],
      description:
        '"D" daily, "W" weekly, "M" monthly, "Q"/"CQ"/"FQ" quarterly, "A"/"CY"/"FY" annual. ' +
        'For multi-year fundamentals (revenue, EPS) prefer "Q", "FQ", or "FY".',
    },
    startDate: {
      type: "string",
      description:
        'Range start. Relative FactSet syntax "-NAY" = N years back (e.g. "-3AY" for the ' +
        'last 3 years), or absolute "MM/DD/YYYY". Default "-1AY" (1 year) — set this ' +
        'explicitly for any other range.',
    },
    endDate: {
      type: "string",
      description: 'Range end. "NOW" (default) or absolute "MM/DD/YYYY".',
    },
    horizon: {
      type: "string",
      enum: ["LTM", "NTM", "FY1", "FY2"],
      description: "Optional financial horizon for estimate-driven charts.",
    },
    annotations: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          content: { type: "string" },
          date: { type: "string" },
          dateTime: { type: "string" },
          entity: { type: "string" },
          isPeerGroup: { type: "boolean" },
        },
        required: ["content", "date"],
      },
      description:
        "Optional chart annotations. Resolve content/date/entity explicitly; do not invent annotations.",
    },
    stylesetId: {
      type: "string",
      description: "Optional FactSet styleset id (from active_graph.list_active_graph_stylesets).",
    },
    mathFunction: {
      type: "string",
      description: "Optional chart math function such as relative_pct.",
    },
    quickLabel: {
      anyOf: [
        {
          type: "string",
          enum: [
            "None",
            "Default",
            "Latest",
            "PercentChange",
            "AbsoluteChange",
            "Avg",
            "Max",
            "Min",
          ],
        },
        { type: "number" },
      ],
      description: "Optional endpoint/quick-label preset.",
    },
    currencies: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          name: { type: "string" },
          iso: { type: "string" },
          isEntity: { type: "boolean" },
        },
        required: ["name", "iso"],
      },
      description: "Optional per-series currency overrides.",
    },
    estimateParameters: {
      type: "object",
      additionalProperties: false,
      properties: {
        period: { type: "string", enum: ["A", "Q"] },
        calendarized: { type: "string" },
        estimatePeriods: { anyOf: [{ type: "string" }, { type: "number" }] },
        actualHistory: { anyOf: [{ type: "string" }, { type: "number" }] },
        statistic: { type: "string" },
        showE: { type: "string" },
        showA: { type: "string" },
      },
      description:
        "Estimate-chart period, history, and forecast settings. On edits include only explicitly requested fields.",
    },
    chartFormat: {
      type: "object",
      additionalProperties: false,
      properties: {
        axisColor: {
          type: "object",
          additionalProperties: { type: "string" },
        },
        fontColor: {
          type: "object",
          additionalProperties: { type: "string" },
        },
        fontHeight: { type: "number" },
        heading1FontHeight: { type: "number" },
        seriesColor: {
          type: "object",
          additionalProperties: { type: "string" },
        },
        xGrid: { type: "boolean" },
        xLabelFormat: { type: "string" },
        yAxisPosition: { type: "string", enum: ["Left", "Right"] },
        yGrid: { type: "boolean" },
        yLabelPrefix: { type: "string" },
      },
      description: "Optional chart formatting and style overrides.",
    },
  },
  required: ["chartType", "tickers"],
};

export const ACTIVE_GRAPH_TOOL: ChartCapabilityToolDef = {
  name: "active_graph",
  description: buildChartToolDescription("active_graph"),
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      stepId: {
        ...nullableString,
        description:
          "Required for create/edit in executor runs. Stable id of the current chart step.",
      },
      workbookName: {
        ...nullableString,
        description:
          "Optional workbook name. Runtime injects the pinned workbook when available; omit unless targeting a known open workbook.",
      },
      operation: {
        type: "string",
        enum: [
          "read_active_graph",
          "create_active_graph",
          "edit_active_graph",
          "list_active_graph_stylesets",
        ],
      },
      chartParameters: FACTSET_CHART_PARAMETERS_SCHEMA,
      sheetRef: {
        ...sheetRefSchema,
        description: "Top-level read_active_graph filter. For edits, use target.sheetRef.",
      },
      chartIndex: {
        ...nullableNumber,
        description: "Top-level read_active_graph chart index filter. For edits, use target.chartIndex.",
      },
      detail: {
        type: "string",
        enum: ["Summary", "Full"],
        description: "Read detail level for read_active_graph. Default is Summary.",
      },
      includeSelectedActiveGraphXml: {
        type: "boolean",
        description:
          "Default false. Does not return raw XML; only reports whether selected XML exists and its length.",
      },
      target: {
        type: "object",
        additionalProperties: false,
        properties: {
          sheetRef: sheetRefSchema,
          chartIndex: nullableNumber,
          coords: {
            type: "object",
            additionalProperties: false,
            description:
              "Optional anchor for the new ActiveGraph. Set top/left for placement. " +
              "New ActiveGraph size is normalized after " +
              "insert so the draggable border fits the rendered chart. When adding " +
              "more than one chart, use a compact grid with modest gaps so charts " +
              "sit near each other without overlapping.",
            properties: {
              top: nullableNumber,
              left: nullableNumber,
            },
          },
          refresh: nullableBoolean,
        },
      },
    },
    required: ["operation"],
  },
};

export const ACTIVE_GRAPH_TOOLS: readonly ChartCapabilityToolDef[] = [
  ACTIVE_GRAPH_TOOL,
];

export async function executeActiveGraphTool(
  rawInput: unknown,
  signal?: AbortSignal,
  userRequest?: string,
): Promise<string> {
  let input: (ActiveGraphInput & { operation: ActiveGraphOperation }) | undefined;
  try {
    input = normalizeActiveGraphInput(rawInput);
    const metricBasisMismatch = activeGraphMetricBasisMismatch(input, userRequest);
    if (metricBasisMismatch) {
      return JSON.stringify(metricBasisMismatch);
    }
    return JSON.stringify(await executeActiveGraphToolOnce(input, signal));
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    if (input && isChartCreatorReauthRequired(error)) {
      try {
        await runOrJoinChartCreatorReauth();
        return JSON.stringify(await executeActiveGraphToolOnce(input, signal));
      } catch (retryError) {
        if (isAbortError(retryError)) {
          throw retryError;
        }
        return JSON.stringify(chartCreatorErrorResult(retryError));
      }
    }
    return JSON.stringify(chartCreatorErrorResult(error));
  }
}

function activeGraphMetricBasisMismatch(
  input: ActiveGraphInput & { operation: ActiveGraphOperation },
  userRequest: string | undefined,
):
  | {
      ok: false;
      code: "active_graph_metric_basis_mismatch";
      error: string;
      chartType: "VAL_PE2";
      requestedMetricBasis: "forward_estimate";
      activeGraphMetricBasis: "trailing_ltm";
      substitutionPerformed: false;
      route: "formula_backed_range_chart";
      instruction: string;
    }
  | undefined {
  if (
    input.operation !== "create_active_graph" &&
    input.operation !== "edit_active_graph"
  ) {
    return undefined;
  }
  const rawChartType = input.chartParameters?.chartType;
  const chartType =
    typeof rawChartType === "string"
      ? rawChartType.trim().toUpperCase()
      : undefined;
  if (chartType !== "VAL_PE2" || !isExplicitForwardPeRequest(userRequest)) {
    return undefined;
  }
  return {
    ok: false,
    code: "active_graph_metric_basis_mismatch",
    error:
      "VAL_PE2 represents trailing/LTM P/E and cannot satisfy the explicit forward/NTM P/E request. " +
      "No substitute ActiveGraph was previewed, edited, or inserted.",
    chartType: "VAL_PE2",
    requestedMetricBasis: "forward_estimate",
    activeGraphMetricBasis: "trailing_ltm",
    substitutionPerformed: false,
    route: "formula_backed_range_chart",
    instruction:
      "Retrieve the exact forward-P/E series with generate_formula, refresh and recalculate FactSet data, " +
      "then read the cells back. Create a range_chart from that verified range only if the requested historical window and " +
      "usable forward-P/E observations are verified; otherwise create no chart and report the no-data outcome explicitly.",
  };
}

async function executeActiveGraphToolOnce(
  input: ActiveGraphInput & { operation: ActiveGraphOperation },
  signal?: AbortSignal,
): Promise<unknown> {
  switch (input.operation) {
    case "read_active_graph":
      return executeReadActiveGraph(input);
    case "create_active_graph":
      return executeCreateActiveGraph(input, signal);
    case "edit_active_graph":
      return executeEditSelectedActiveGraph(input, signal);
    case "list_active_graph_stylesets":
      return {
        ok: true,
        ...(await ((await shouldUseChartCreatorApi())
          ? getStylesetsViaApi({ signal })
          : getStylesetsViaIntentBridge({ signal }))),
      };
    default:
      return { ok: false, error: `Unsupported operation: ${input.operation}` };
  }
}

function chartCreatorErrorResult(error: unknown): { ok: false; error: string } {
  return {
    ok: false,
    error: error instanceof Error ? error.message : String(error),
  };
}

function isChartCreatorReauthRequired(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === "ChartCreatorReauthRequiredError"
  );
}

async function executeReadActiveGraph(rawInput: unknown): Promise<Record<string, unknown>> {
  try {
    const input = normalizeReadChartsInput(rawInput);
    const charts = (await getCharts({
      workbookName: input.workbookName,
      sheetRef: input.sheetRef,
      type: "OLEObject",
      chartIndex: input.chartIndex,
      detail: input.detail,
    })).filter(isActiveGraphChartInfo);
    const result: Record<string, unknown> = {
      ok: true,
      total: charts.length,
      charts: charts.map(trimChartInfo),
      note:
        "Raw FDSChart XML is intentionally not returned. Use active_graph.edit_active_graph for selected ActiveGraph edits or pass sheetRef + chartIndex from this result.",
    };
    if (input.includeSelectedActiveGraphXml) {
      result.selectedActiveGraphXml = await selectedActiveGraphSummary();
    }
    return result;
  } catch (error) {
    return chartCreatorErrorResult(error);
  }
}

function normalizeActiveGraphInput(rawInput: unknown): ActiveGraphInput & {
  operation: ActiveGraphOperation;
} {
  const obj = rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
    ? (rawInput as ActiveGraphInput)
    : {};
  const operation = obj.operation;
  if (!operation) throw new Error("active_graph requires an operation.");
  return {
    ...obj,
    operation,
    workbookName: typeof obj.workbookName === "string" ? obj.workbookName : undefined,
    target: normalizeChartCreatorTarget(obj.target),
  };
}

function sanitizeSheetRef(
  sheetRef: unknown,
): { index?: number; name?: string; codeName?: string } | undefined {
  if (!sheetRef || typeof sheetRef !== "object" || Array.isArray(sheetRef)) {
    return undefined;
  }
  const ref = sheetRef as { index?: unknown; name?: unknown; codeName?: unknown };
  const out: { index?: number; name?: string; codeName?: string } = {};
  if (typeof ref.index === "number" && Number.isFinite(ref.index)) out.index = ref.index;
  if (typeof ref.name === "string" && ref.name.trim().length > 0) out.name = ref.name;
  if (typeof ref.codeName === "string" && ref.codeName.trim().length > 0) {
    out.codeName = ref.codeName;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function sanitizeActiveGraphCoords(coords: unknown): AddActiveGraphCoords | undefined {
  if (!coords || typeof coords !== "object" || Array.isArray(coords)) {
    return undefined;
  }
  const raw = coords as {
    top?: unknown;
    left?: unknown;
  };
  const out: AddActiveGraphCoords = {};
  if (typeof raw.top === "number" && Number.isFinite(raw.top)) out.top = raw.top;
  if (typeof raw.left === "number" && Number.isFinite(raw.left)) out.left = raw.left;
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeChartCreatorTarget(
  target: ActiveGraphInput["target"],
): ActiveGraphInput["target"] {
  if (!target || typeof target !== "object" || Array.isArray(target)) {
    return undefined;
  }
  const next: NonNullable<ActiveGraphInput["target"]> = { ...target };
  const sheetRef = sanitizeSheetRef(target.sheetRef);
  if (sheetRef) {
    next.sheetRef = sheetRef;
  } else {
    delete next.sheetRef;
  }
  const coords = sanitizeActiveGraphCoords(target.coords);
  if (coords) {
    next.coords = coords;
  } else {
    delete next.coords;
  }
  if (typeof next.chartIndex !== "number" || !Number.isFinite(next.chartIndex)) {
    delete next.chartIndex;
  }
  return next;
}

function normalizeReadChartsInput(rawInput: unknown): ReadChartsInput {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) return {};
  const obj = rawInput as ReadChartsInput;
  return {
    workbookName: typeof obj.workbookName === "string" ? obj.workbookName : undefined,
    sheetRef: sanitizeSheetRef(obj.sheetRef),
    chartIndex: typeof obj.chartIndex === "number" ? obj.chartIndex : undefined,
    detail: obj.detail === "Summary" || obj.detail === "Full" ? obj.detail : undefined,
    includeSelectedActiveGraphXml: obj.includeSelectedActiveGraphXml === true,
  };
}

function isActiveGraphChartInfo(chart: ChartingChartInfo): boolean {
  return chart.type === "OLEObject" && chart.isActiveGraph === true;
}

function requireChartParameters(input: ActiveGraphInput): FactsetChartParameters {
  if (!input.chartParameters) {
    throw new Error(`${input.operation} requires chartParameters.`);
  }
  const params = normalizeChartParametersForTool(input.chartParameters);
  assertActiveGraphEntityCardinality(params);
  return params;
}

function assertActiveGraphEntityCardinality(params: FactsetChartParameters): void {
  const chartType = params.chartType ?? "";
  const constraint = getActiveGraphEntityConstraint(chartType);
  if (!constraint) return;

  const tickers = params.tickers ?? [];
  const tickerCount = tickers.length;
  const tooFew = tickerCount < constraint.minTickers;
  const tooMany = constraint.maxTickers !== undefined && tickerCount > constraint.maxTickers;
  if (!tooFew && !tooMany) return;

  const received =
    tickerCount === 1
      ? "1 ticker"
      : `${tickerCount} tickers`;
  const tickerList = tickers.length > 0 ? ` (${tickers.join(", ")})` : "";
  const guidance = tooMany
    ? "Choose a comparison-capable ActiveGraph type for multiple companies, or create separate ActiveGraphs."
    : "Provide the required ticker count, or choose an ActiveGraph type that matches the requested entity count.";
  throw new Error(
    `${chartType} supports ${constraint.label}; received ${received}${tickerList}. ` +
      guidance,
  );
}

function normalizeChartParametersForTool(
  params: FactsetChartParameters,
): FactsetChartParameters {
  return normalizeActiveGraphChartParameters(params);
}

function getPreviewBusinessError(preview: { error?: string | null }): string | undefined {
  if (typeof preview.error !== "string") return undefined;
  return preview.error.trim().length > 0 ? preview.error : undefined;
}

/**
 * Shape frame size applied after insertion so the ActiveGraph border fits the
 * rendered chart.
 */
const ACTIVE_GRAPH_FRAME_POINTS = { width: 300, height: 180 } as const;

/**
 * Captures current ActiveGraph OLE objects before insertion. Returns `null`
 * when chart metadata cannot be read, making later frame adjustment best-effort.
 */
async function readActiveGraphsForFrameAdjustment(workbookName?: string): Promise<ChartingChartInfo[] | null> {
  try {
    return (await getCharts({
      workbookName,
      type: "OLEObject",
      detail: "Summary",
    })).filter(isActiveGraphChartInfo);
  } catch {
    return null;
  }
}

/**
 * Extracts the numeric shape id used by Excel shape APIs, when available.
 */
function numericChartId(chart: ChartingChartInfo): number | undefined {
  return typeof chart.id === "number" && Number.isFinite(chart.id) ? chart.id : undefined;
}

/**
 * Reads the worksheet name needed to target shape edits on a specific sheet.
 */
function sheetNameForShape(chart: ChartingChartInfo): string | undefined {
  const name = chart.sheetRef?.name;
  return typeof name === "string" && name.trim().length > 0 ? name : undefined;
}

/**
 * Compares pre/post insertion snapshots and returns the newly inserted
 * ActiveGraph, if it can be identified by id.
 */
function findInsertedActiveGraph(
  before: readonly ChartingChartInfo[] | null,
  after: readonly ChartingChartInfo[],
): ChartingChartInfo | undefined {
  if (!before) return undefined;
  const beforeIds = new Set(before.map(numericChartId).filter((id): id is number => id !== undefined));
  return after.find((chart) => {
    const id = numericChartId(chart);
    return id !== undefined && !beforeIds.has(id);
  });
}

/**
 * Resizes the newly inserted ActiveGraph frame. Failures are returned as
 * warnings so successful chart creation is not downgraded to an error.
 */
async function normalizeInsertedActiveGraphFrame(
  before: readonly ChartingChartInfo[] | null,
  workbookName?: string,
): Promise<{ frameAdjusted?: boolean; frameAdjustmentWarning?: string }> {
  if (!before) return {};
  try {
    const after = await readActiveGraphsForFrameAdjustment(workbookName);
    if (!after) return {};
    const inserted = findInsertedActiveGraph(before, after);
    const id = inserted ? numericChartId(inserted) : undefined;
    if (id === undefined) return {};
    const sheetName = sheetNameForShape(inserted);
    if (workbookName && !sheetName) {
      return {
        frameAdjustmentWarning:
          "Inserted ActiveGraph sheet could not be resolved; skipped frame normalization for the pinned workbook.",
      };
    }
    await foregroundCoordinator.runInForeground(
      { workbookName, sheet: sheetName },
      async () => {
        await modifyShapes([
          {
            shape: {
              id,
              ...(sheetName ? { sheetName } : {}),
            },
            width: ACTIVE_GRAPH_FRAME_POINTS.width,
            height: ACTIVE_GRAPH_FRAME_POINTS.height,
          },
        ]);
      },
      { contextLabel: "chart creator ActiveGraph frame normalization" },
    );
    return { frameAdjusted: true };
  } catch (error) {
    return {
      frameAdjustmentWarning:
        error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Reads the explicit target sheet name supplied for a create operation.
 */
function sheetNameForCreate(input: ActiveGraphInput): string | undefined {
  const name = input.target?.sheetRef?.name;
  return typeof name === "string" && name.trim().length > 0 ? name : undefined;
}

/**
 * Inserts ActiveGraph XML after foregrounding the target workbook and sheet.
 * Pinned workbook writes require a sheet name to avoid targeting the wrong
 * active workbook.
 */
async function addActiveGraphInTargetWorkbook(
  xml: string,
  input: ActiveGraphInput,
): Promise<string> {
  const sheetName = sheetNameForCreate(input);
  if (input.workbookName && !sheetName) {
    throw new Error(
      "Pinned workbook ActiveGraph creation requires target.sheetRef.name so the target workbook can be activated safely.",
    );
  }
  return foregroundCoordinator.runInForeground(
    { workbookName: input.workbookName, sheet: sheetName },
    () => addActiveGraph(xml, input.target?.coords),
    { contextLabel: "chart creator ActiveGraph insertion" },
  );
}

async function executeCreateActiveGraph(input: ActiveGraphInput, signal?: AbortSignal) {
  const chartParameters = requireChartParameters(input);
  const beforeActiveGraphs = await readActiveGraphsForFrameAdjustment(input.workbookName);
  const preview = await previewChartForMode(chartParameters, {
    signal,
    isEdit: false,
  });
  const xml = preview.fdsChartXml;
  const previewError = getPreviewBusinessError(preview);
  if (previewError !== undefined) return { ok: false, error: previewError };
  if (!xml) return { ok: false, error: "Chart Creator returned no FDSChart XML." };
  const message = await addActiveGraphInTargetWorkbook(xml, input);
  const frameAdjustment = await normalizeInsertedActiveGraphFrame(beforeActiveGraphs, input.workbookName);
  const afterActiveGraphs = await readActiveGraphsForFrameAdjustment(input.workbookName);
  const targetSheet = sheetNameForCreate(input);
  const inserted =
    afterActiveGraphs &&
    (findInsertedActiveGraph(beforeActiveGraphs, afterActiveGraphs) ??
      (beforeActiveGraphs === null
        ? afterActiveGraphs.find(
            (chart) =>
              !targetSheet ||
              chart.sheetRef?.name?.toLowerCase() === targetSheet.toLowerCase(),
          )
        : undefined));
  if (!inserted) {
    return {
      ok: false,
      applied: false,
      partialMutation: true,
      error:
        "ActiveGraph insertion was acknowledged, but GetCharts did not confirm a new OLE ActiveGraph on the intended target.",
      completionEvidence: {
        satisfied: false,
        previewSucceeded: true,
        insertionSucceeded: true,
        activeGraphReadBack: false,
      },
    };
  }
  return {
    ok: true,
    applied: true,
    action: "created_active_graph",
    message,
    ...frameAdjustment,
    fdsChartXmlAvailable: true,
    fdsChartXmlLength: xml.length,
    activeGraph: trimChartInfo(inserted),
    completionEvidence: {
      satisfied: true,
      previewSucceeded: true,
      insertionSucceeded: true,
      activeGraphReadBack: true,
      objectType: "OLEObject",
      isActiveGraph: true,
    },
  };
}

function hasExplicitEditTarget(target: ActiveGraphInput["target"]): boolean {
  return Boolean(target?.sheetRef && typeof target.chartIndex === "number");
}

async function preflightSelectedActiveGraph(input: ActiveGraphInput): Promise<
  | { ok: true }
  | { ok: false; error: string }
> {
  if (hasExplicitEditTarget(input.target)) return { ok: true };
  try {
    const selected = await getActiveGraphXml();
    const xml = selected.fdsChartXml;
    if (typeof xml === "string" && xml.trim().length > 0) {
      return { ok: true };
    }
    return {
      ok: false,
      error:
        "No selected FactSet ActiveGraph was found. Select an ActiveGraph first, or call active_graph.read_active_graph and pass target.sheetRef plus target.chartIndex.",
    };
  } catch (error) {
    return {
      ok: false,
      error:
        "No selected FactSet ActiveGraph was found. Select an ActiveGraph first, or call active_graph.read_active_graph and pass target.sheetRef plus target.chartIndex. " +
        `(Host error: ${error instanceof Error ? error.message : String(error)})`,
    };
  }
}

async function executeEditSelectedActiveGraph(input: ActiveGraphInput, signal?: AbortSignal) {
  const chartParameters = requireChartParameters(input);
  const preflight = await preflightSelectedActiveGraph(input);
  if (!preflight.ok) return preflight;

  const preview = await previewChartForMode(chartParameters, {
    signal,
    isEdit: true,
  });
  const xml = preview.fdsChartXml;
  const previewError = getPreviewBusinessError(preview);
  if (previewError !== undefined) return { ok: false, error: previewError };
  if (!xml) return { ok: false, error: "Chart Creator returned no FDSChart XML." };
  const options: SetActiveGraphXmlOptions = {
    refresh: input.target?.refresh ?? true,
    sheetRef: input.target?.sheetRef,
    chartIndex: input.target?.chartIndex,
  };
  if (input.workbookName) {
    options.workbookName = input.workbookName;
  }
  const message = await setActiveGraphXml(xml, options);
  const readBack = (await getCharts({
    workbookName: input.workbookName,
    sheetRef: input.target?.sheetRef,
    type: "OLEObject",
    ...(typeof input.target?.chartIndex === "number"
      ? { chartIndex: input.target.chartIndex }
      : {}),
    detail: "Summary",
  })).filter(isActiveGraphChartInfo);
  const selectedXmlReadBack = hasExplicitEditTarget(input.target)
    ? true
    : await selectedActiveGraphXmlExists();
  if (readBack.length === 0 || !selectedXmlReadBack) {
    return {
      ok: false,
      applied: false,
      partialMutation: true,
      error:
        "ActiveGraph update was acknowledged, but GetCharts did not confirm the OLE ActiveGraph target.",
      completionEvidence: {
        satisfied: false,
        previewSucceeded: true,
        updateSucceeded: true,
        activeGraphReadBack: false,
        selectedXmlReadBack,
      },
    };
  }
  return {
    ok: true,
    applied: true,
    action: hasExplicitEditTarget(input.target)
      ? "updated_target_active_graph"
      : "updated_selected_active_graph",
    message,
    fdsChartXmlAvailable: true,
    fdsChartXmlLength: xml.length,
    activeGraph: trimChartInfo(readBack[0]),
    completionEvidence: {
      satisfied: true,
      previewSucceeded: true,
      updateSucceeded: true,
      activeGraphReadBack: true,
      selectedXmlReadBack,
      objectType: "OLEObject",
      isActiveGraph: true,
    },
  };
}

async function selectedActiveGraphXmlExists(): Promise<boolean> {
  try {
    const selected = await getActiveGraphXml();
    return (
      typeof selected.fdsChartXml === "string" &&
      selected.fdsChartXml.trim().length > 0
    );
  } catch {
    return false;
  }
}

async function previewChartForMode(
  parameters: FactsetChartParameters,
  options: { signal?: AbortSignal; isEdit: boolean },
) {
  const useApi = await shouldUseChartCreatorApi();
  const preview = await (useApi ? previewChartViaApi : previewChartViaIntentBridge)(
    parameters,
    {
      signal: options.signal,
      isEdit: options.isEdit,
      generatePreview: "None",
    },
  );
  return preview;
}

async function shouldUseChartCreatorApi(): Promise<boolean> {
  const resolution = await resolveChartCreatorMode();
  return resolution.mode === "api";
}

async function selectedActiveGraphSummary(): Promise<Record<string, unknown>> {
  try {
    const active = await getActiveGraphXml();
    return {
      available: typeof active.fdsChartXml === "string" && active.fdsChartXml.length > 0,
      length: typeof active.fdsChartXml === "string" ? active.fdsChartXml.length : 0,
    };
  } catch (error) {
    return {
      available: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function trimChartInfo(chart: ChartingChartInfo): Record<string, unknown> {
  return {
    type: chart.type,
    id: chart.id,
    index: chart.index,
    sheetRef: chart.sheetRef,
    name: chart.name,
    isActiveGraph: chart.isActiveGraph,
    progId: chart.progId,
    chartType: chart.chartType,
    title: chart.title?.text,
    position: chart.position,
    series: chart.series?.map((series) => ({
      index: series.index,
      name: series.name,
      values: series.values,
      categories: series.categories,
      chartType: series.chartType,
    })),
    axes: chart.axes?.map((axis) => ({
      axisType: axis.axisType,
      titleText: axis.titleText,
      minimumScale: axis.minimumScale,
      maximumScale: axis.maximumScale,
      numberFormat: axis.numberFormat,
    })),
  };
}
